import yaml from 'js-yaml';
import type { Card, GameClass, TooltipData, Cost, Requirements, Effect, ThinkingActionsData, RegretsData, RegretsChoice, StartingResources } from '../types';

// Transform YAML cost array format to object format
function transformCost(costArray: any[] | undefined): Cost | undefined {
  if (!costArray || !Array.isArray(costArray) || costArray.length === 0) {
    return undefined;
  }
  
  const cost: Cost = {};
  for (const item of costArray) {
    if (typeof item === 'object' && item !== null) {
      // Each item is like { time: 2 } or { money: 1, time: 2 }
      Object.assign(cost, item);
    }
  }
  
  return Object.keys(cost).length > 0 ? cost : undefined;
}

// Transform YAML requirements array format to object format
function transformRequirements(reqArray: any[] | undefined): Requirements | undefined {
  if (!reqArray || !Array.isArray(reqArray) || reqArray.length === 0) {
    return undefined;
  }
  
  const requirements: Requirements = {};
  for (const item of reqArray) {
    if (typeof item === 'object' && item !== null) {
      // Each item is like { smarts: 4 } or { tenacity: 1, social: 1 }
      Object.assign(requirements, item);
    }
  }
  
  return Object.keys(requirements).length > 0 ? requirements : undefined;
}

// Transform YAML effects array format to Effect array format
function transformEffects(effectsArray: any[] | undefined): Effect[] | undefined {
  if (!effectsArray || !Array.isArray(effectsArray) || effectsArray.length === 0) {
    return undefined;
  }
  
  // Each item in the array is a single-key object like { health: -1 } or { exhaust_this_event: true }
  // We keep them as separate effect objects
  const effects: Effect[] = [];
  for (const item of effectsArray) {
    if (typeof item === 'object' && item !== null) {
      effects.push(item as Effect);
    }
  }
  
  return effects.length > 0 ? effects : undefined;
}

// Transform a raw card from YAML to the expected Card format
function transformCard(rawCard: any): Card {
  const card: Card = {
    name: rawCard.name || '',
    description: rawCard.description || '',
    options: []
  };
  
  if (rawCard.copies !== undefined) {
    card.copies = rawCard.copies;
  }
  
  if (rawCard.options && Array.isArray(rawCard.options)) {
    card.options = rawCard.options.map((rawOption: any) => ({
      name: rawOption.name || '',
      cost: transformCost(rawOption.cost),
      requirements: transformRequirements(rawOption.requirements),
      effects: transformEffects(rawOption.effects)
    }));
  }
  
  return card;
}

export async function loadCards(): Promise<Card[]> {
  const cards: Card[] = [];
  
  // Load regular cards
  const regularResponse = await fetch('./data/cards/regular_cards.yaml');
  const regularText = await regularResponse.text();
  const regularData = yaml.load(regularText) as any;
  if (regularData.regular_cards && Array.isArray(regularData.regular_cards)) {
    cards.push(...regularData.regular_cards.map(transformCard));
  }
  
  // Load habit cards
  const habitResponse = await fetch('./data/cards/habit_cards_in_deck.yaml');
  const habitText = await habitResponse.text();
  const habitData = yaml.load(habitText) as any;
  if (habitData.habit_cards && Array.isArray(habitData.habit_cards)) {
    cards.push(...habitData.habit_cards.map(transformCard));
  }
  
  // Load sleep cards
  const sleepResponse = await fetch('./data/cards/sleep_cards_in_deck.yaml');
  const sleepText = await sleepResponse.text();
  const sleepData = yaml.load(sleepText) as any;
  if (sleepData.sleep_cards && Array.isArray(sleepData.sleep_cards)) {
    cards.push(...sleepData.sleep_cards.map(transformCard));
  }
  
  // Load special unlock cards
  const specialResponse = await fetch('./data/cards/special_unlock_cards_in_deck.yaml');
  const specialText = await specialResponse.text();
  const specialData = yaml.load(specialText) as any;
  if (specialData.special_unlock_cards && Array.isArray(specialData.special_unlock_cards)) {
    cards.push(...specialData.special_unlock_cards.map(transformCard));
  }
  
  return cards;
}

// Transform starting skills from array format to object format
function transformStartingSkills(skillsArray: any[] | undefined): { smarts: number; tenacity: number; social: number } {
  const skills = { smarts: 0, tenacity: 0, social: 0 };
  
  if (skillsArray && Array.isArray(skillsArray)) {
    for (const item of skillsArray) {
      if (typeof item === 'object' && item !== null) {
        if (item.smarts !== undefined) skills.smarts = item.smarts;
        if (item.tenacity !== undefined) skills.tenacity = item.tenacity;
        if (item.social !== undefined) skills.social = item.social;
      }
    }
  }
  
  return skills;
}

// Transform starting bonus resources from array format to object format
function transformStartingBonusResources(bonusesArray: any[] | undefined): any {
  const bonuses: any = {};
  
  if (bonusesArray && Array.isArray(bonusesArray)) {
    for (const item of bonusesArray) {
      if (typeof item === 'object' && item !== null) {
        Object.assign(bonuses, item);
      }
    }
  }
  
  return bonuses;
}

export async function loadClasses(): Promise<GameClass[]> {
  const response = await fetch('./data/classes.yaml');
  const text = await response.text();
  const data = yaml.load(text) as any;
  
  const classes = data.classe || [];
  
  // Transform each class
  return classes.map((rawClass: any) => ({
    name: rawClass.name || '',
    description: rawClass.description || '',
    starting_skills: transformStartingSkills(rawClass.starting_skills),
    starting_bonus_resources: transformStartingBonusResources(rawClass.starting_bonus_resources)
  }));
}

export async function loadTooltips(): Promise<TooltipData> {
  try {
    const response = await fetch('./data/tooltips.yaml');
    const text = await response.text();
    const data = yaml.load(text) as TooltipData;
    return data || {};
  } catch (error) {
    console.warn('Could not load tooltips.yaml, using empty tooltips');
    return {};
  }
}


export async function loadOpportunityCards(): Promise<Card[]> {
  try {
    const response = await fetch('./data/cards/opportunity_card.yaml');
    const text = await response.text();
    const data = yaml.load(text) as any;
    const opportunityCards = data.opportunity_cards || [];
    return Array.isArray(opportunityCards) ? opportunityCards.map(transformCard) : [];
  } catch (error) {
    console.warn('Could not load opportunity_card.yaml');
    return [];
  }
}

export async function loadThinkingActions(): Promise<ThinkingActionsData> {
  try {
    const response = await fetch('./data/thinking_actions.yaml');
    const text = await response.text();
    const data = yaml.load(text) as any;
    
    // Transform thinking actions (similar to cards but simpler)
    const transformThinkingAction = (action: any) => ({
      name: action.name || '',
      description: action.description || '',
      cost: transformCost(action.cost),
      requirements: transformRequirements(action.requirements),
      effects: transformEffects(action.effects),
      one_time: action.one_time || false
    });
    
    return {
      always_available: (data.always_available || []).map(transformThinkingAction),
      unlocked_actions: (data.unlocked_actions || []).map(transformThinkingAction)
    };
  } catch (error) {
    console.warn('Could not load thinking_actions.yaml');
    return {
      always_available: [],
      unlocked_actions: []
    };
  }
}

export async function loadHelpTexts(): Promise<any> {
  try {
    const response = await fetch('./data/help_texts.yaml');
    const text = await response.text();
    const data = yaml.load(text) as any;
    return data.help_modal || {};
  } catch (error) {
    console.warn('Could not load help_texts.yaml, using default help texts');
    return {};
  }
}

export async function loadRegrets(): Promise<RegretsData> {
  try {
    const response = await fetch('./data/regrets.yaml');
    const text = await response.text();
    const data = yaml.load(text) as any;
    
    const regretsData = data.regrets_phase;
    
    // Transform choices (for step 3 and 4)
    const transformChoice = (choice: any): RegretsChoice => ({
      name: choice.name || '',
      cost: transformCost(choice.cost),
      effects: transformEffects(choice.effects)
    });
    
    return {
      step1_lose_health: {
        title: regretsData.step1_lose_health.title || 'Regrets Phase',
        health_loss_base: regretsData.step1_lose_health.health_loss_base ?? 2,
        message_template: regretsData.step1_lose_health.message_template || ''
      },
      step2_pay_bills: {
        title: regretsData.step2_pay_bills.title || 'Bills Due',
        required_money: regretsData.step2_pay_bills.required_money ?? 9,
        health_loss_per_unpaid_money: regretsData.step2_pay_bills.health_loss_per_unpaid_money ?? 1,
        message_templates: {
          start: regretsData.step2_pay_bills.message_templates?.start || '',
          paid: regretsData.step2_pay_bills.message_templates?.paid || '',
          shortfall: regretsData.step2_pay_bills.message_templates?.shortfall || '',
          covered: regretsData.step2_pay_bills.message_templates?.covered || ''
        }
      },
      step3_basic_needs: {
        title: regretsData.step3_basic_needs.title || 'Basic Needs',
        message: regretsData.step3_basic_needs.message || '',
        choices: (regretsData.step3_basic_needs.choices || []).map(transformChoice)
      },
      step4_connections: {
        title: regretsData.step4_connections.title || 'Social Obligations',
        message: regretsData.step4_connections.message || '',
        choices: (regretsData.step4_connections.choices || []).map(transformChoice)
      }
    };
  } catch (error) {
    console.warn('Could not load regrets.yaml, using defaults');
    // Return default values if file can't be loaded
    return {
      step1_lose_health: {
        title: 'Regrets Phase',
        health_loss_base: 2,
        message_template: 'Your bad habits catch up with you. You lose {healthLoss} Health.'
      },
      step2_pay_bills: {
        title: 'Bills Due',
        required_money: 9,
        health_loss_per_unpaid_money: 1,
        message_templates: {
          start: 'You must pay {requiredMoney} Money for bills. ',
          paid: 'You paid {moneyPaid} Money. ',
          shortfall: 'You couldn\'t pay {moneyShort} Money. You lose {healthLoss} Health (1 Health per unpaid Money).',
          covered: 'You managed to cover all your bills.'
        }
      },
      step3_basic_needs: {
        title: 'Basic Needs',
        message: 'You must cover your basic needs. What will you sacrifice?',
        choices: [
          {
            name: 'Pay 2 Money, 1 Food, and lose 1 Health',
            cost: { money: 2, food: 1 },
            effects: [{ health: -1 }]
          },
          {
            name: 'Lose 1 Sanity',
            cost: undefined,
            effects: [{ sanity: -1 }]
          }
        ]
      },
      step4_connections: {
        title: 'Social Obligations',
        message: 'You must maintain your connections or face the consequences.',
        choices: [
          {
            name: 'Pay 1 Connection',
            cost: { connections: 1 },
            effects: []
          },
          {
            name: 'Lose 1 Sanity',
            cost: undefined,
            effects: [{ sanity: -1 }]
          }
        ]
      }
    };
  }
}

export async function loadGameName(): Promise<string> {
  try {
    const response = await fetch('./data/name.yaml');
    const text = await response.text();
    const data = yaml.load(text) as any;
    return data.name || 'Choose Your Path';
  } catch (error) {
    console.warn('Could not load name.yaml, using default');
    return 'Choose Your Path';
  }
}

export async function loadStartingResources(): Promise<StartingResources> {
  try {
    const response = await fetch('./data/starting_resources.yaml');
    const text = await response.text();
    const data = yaml.load(text) as any;
    const resources = data.starting_resources || {};
    
    return {
      money: resources.money ?? 11,
      food: resources.food ?? 0,
      motivation: resources.motivation ?? 0,
      memory: resources.memory ?? 0,
      connections: resources.connections ?? 0,
      health: resources.health ?? 4,
      bad_habit: resources.bad_habit ?? 9,
      sanity: resources.sanity ?? 5,
      dignity: resources.dignity ?? 0
    };
  } catch (error) {
    console.warn('Could not load starting_resources.yaml, using defaults');
    return {
      money: 11,
      food: 0,
      motivation: 0,
      memory: 0,
      connections: 0,
      health: 4,
      bad_habit: 9,
      sanity: 5,
      dignity: 0
    };
  }
}

